import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';
import { useSocket } from '../hooks/useSocket';

// ── Design tokens ─────────────────────────────────────────────────
const C = {
  bg:     '#0f172a', bg2: '#1e293b', border: '#334155',
  text:   '#f1f5f9', sub: '#94a3b8', muted: '#475569',
  blue:   '#0ea5e9', green: '#22c55e', yellow: '#f59e0b',
  red:    '#ef4444', purple: '#8b5cf6'
};

const s = {
  title:  { fontSize:22, fontWeight:600, color:C.text, marginBottom:4 },
  sub:    { fontSize:13, color:C.muted, marginBottom:24 },
  tabs:   { display:'flex', gap:0, marginBottom:24, borderBottom:`1px solid ${C.border}` },
  tab:    (a) => ({ padding:'9px 20px', cursor:'pointer', fontSize:13, fontWeight:500,
             color:a?C.blue:C.sub, background:'none', border:'none',
             borderBottom:`2px solid ${a?C.blue:'transparent'}` }),
  card:   { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:16 },
  grid2:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 },
  grid3:  { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 },
  grid4:  { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 },
  label:  { fontSize:12, color:C.sub, marginBottom:5, display:'block' },
  input:  { width:'100%', background:C.bg, border:`1px solid ${C.border}`,
             borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
             outline:'none', marginBottom:13, fontFamily:'inherit' },
  tarea:  { width:'100%', background:C.bg, border:`1px solid ${C.border}`,
             borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
             outline:'none', marginBottom:13, resize:'vertical', minHeight:70,
             fontFamily:'inherit' },
  sel:    { width:'100%', background:C.bg, border:`1px solid ${C.border}`,
             borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13, marginBottom:13 },
  btn:    { background:C.blue, color:'#fff', border:'none', borderRadius:8,
             padding:'9px 20px', cursor:'pointer', fontSize:13, fontWeight:600 },
  btnSm:  { background:C.bg2, color:C.sub, border:`1px solid ${C.border}`,
             borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12 },
  btnGrn: { background:C.green, color:'#fff', border:'none', borderRadius:8,
             padding:'7px 16px', cursor:'pointer', fontSize:12, fontWeight:600 },
  btnYel: { background:C.yellow, color:C.bg, border:'none', borderRadius:8,
             padding:'7px 16px', cursor:'pointer', fontSize:12, fontWeight:600 },
  btnRed: { background:'transparent', color:C.red, border:`1px solid rgba(239,68,68,.3)`,
             borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:12 },
  kpi:    { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, padding:16 },
  kpiV:   (c=C.blue) => ({ fontSize:28, fontWeight:700, color:c }),
  kpiL:   { fontSize:11, color:C.muted, marginTop:2 },
  badge:  (c) => ({ background:c+'22', color:c, padding:'3px 10px',
                    borderRadius:12, fontSize:11, fontWeight:600 }),
  table:  { width:'100%', borderCollapse:'collapse' },
  th:     { fontSize:11, color:C.muted, textAlign:'left', padding:'7px 8px',
             borderBottom:`1px solid ${C.border}`, textTransform:'uppercase' },
  td:     { fontSize:12, color:'#cbd5e1', padding:'8px', borderBottom:`1px solid #1e293b` },
  prog:   { height:8, background:'#1e293b', borderRadius:4, overflow:'hidden', marginTop:4 },
  progFill:(pct,c=C.blue) => ({ height:'100%', width:`${Math.min(pct,100)}%`,
                                  background:c, borderRadius:4, transition:'width .5s' }),
  err:    { background:'rgba(239,68,68,.1)', color:'#f87171', borderRadius:8, padding:'10px 14px',
             fontSize:13, marginBottom:12, border:'1px solid rgba(239,68,68,.25)' },
  ok:     { background:'rgba(34,197,94,.1)', color:'#4ade80', borderRadius:8, padding:'10px 14px',
             fontSize:13, marginBottom:12, border:'1px solid rgba(34,197,94,.25)' },
  uploadBox: { border:`2px dashed ${C.border}`, borderRadius:10, padding:'24px', textAlign:'center',
               cursor:'pointer', color:C.muted, fontSize:13, marginBottom:13 },
  hr:     { border:'none', borderTop:`1px solid ${C.border}`, margin:'18px 0' },
};

const STATUS_COLOR = {
  rascunho:'#64748b', aguardando:C.yellow, rodando:C.green,
  pausada:C.yellow, concluida:C.blue, cancelada:C.red
};
const STATUS_PT = {
  rascunho:'Rascunho', aguardando:'Aguardando', rodando:'Rodando',
  pausada:'Pausada', concluida:'Concluída', cancelada:'Cancelada'
};
const CONT_COLOR = {
  pendente:'#64748b', enviado:C.blue, erro:C.red,
  respondeu_sim:C.green, respondeu_nao:C.red, respondeu_outro:C.yellow, opt_out:'#64748b'
};
const ALL_ACTIVE_INSTANCES = '__ALL_ACTIVE__';

function nomeInstanciaCampanha(value) {
  return value === ALL_ACTIVE_INSTANCES ? 'Todos os números ativos' : value;
}

function KpiCard({ value, label, color }) {
  return (
    <div style={s.kpi}>
      <div style={s.kpiV(color)}>{value ?? '–'}</div>
      <div style={s.kpiL}>{label}</div>
    </div>
  );
}

function ProgressBar({ value, max, color }) {
  const pct = max ? (value / max) * 100 : 0;
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:2 }}>
        <span>{value} / {max}</span><span>{pct.toFixed(0)}%</span>
      </div>
      <div style={s.prog}><div style={s.progFill(pct, color)} /></div>
    </div>
  );
}

// ── Aba Templates ─────────────────────────────────────────────────
function AbaTemplates() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm]           = useState({ nome:'', tipo:'texto', conteudo:'', caption:'' });
  const [arquivo, setArquivo]     = useState(null);
  const [msg, setMsg]             = useState(null);
  const [saving, setSaving]       = useState(false);

  const carregar = () => api.get('/campanhas/templates').then(r => setTemplates(r.data));
  useEffect(() => { carregar(); }, []);

  const salvar = async () => {
    setSaving(true); setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      if (arquivo) fd.append('midia', arquivo);
      await api.post('/campanhas/templates', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
      setMsg({ ok:true, txt:'Template salvo!' });
      setForm({ nome:'', tipo:'texto', conteudo:'', caption:'' });
      setArquivo(null);
      carregar();
    } catch (e) { setMsg({ ok:false, txt: e.response?.data?.error || 'Erro' }); }
    finally { setSaving(false); }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover template?')) return;
    await api.delete(`/campanhas/templates/${id}`);
    carregar();
  };

  return (
    <div style={s.grid2}>
      <div>
        <div style={s.card}>
          <div style={{ fontSize:14, fontWeight:600, color:C.sub, marginBottom:14 }}>Novo template</div>
          {msg && <div style={msg.ok?s.ok:s.err}>{msg.txt}</div>}
          <label style={s.label}>Nome do template</label>
          <input style={s.input} value={form.nome} placeholder="ex: Oferta Curso D1"
            onChange={e=>setForm(f=>({...f,nome:e.target.value}))} />
          <label style={s.label}>Tipo</label>
          <select style={s.sel} value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))}>
            <option value="texto">Texto</option>
            <option value="imagem">Imagem</option>
            <option value="documento">Documento / PDF</option>
            <option value="audio">Áudio</option>
            <option value="video">Vídeo</option>
          </select>
          <label style={s.label}>
            Mensagem / legenda
            <span style={{color:C.muted,fontWeight:400,marginLeft:6}}>use {'{nome}'} {'{empresa}'} {'{cidade}'}</span>
          </label>
          <textarea style={s.tarea} value={form.conteudo}
            placeholder="Olá, {nome}! Temos uma novidade especial para você..."
            onChange={e=>setForm(f=>({...f,conteudo:e.target.value}))} />
          {form.tipo !== 'texto' && (
            <>
              <label style={s.label}>Arquivo de mídia</label>
              <div style={s.uploadBox} onClick={() => document.getElementById('arq-upl').click()}>
                {arquivo ? `📎 ${arquivo.name}` : 'Clique para selecionar o arquivo'}
                <input id="arq-upl" type="file" style={{ display:'none' }}
                  onChange={e => setArquivo(e.target.files[0])} />
              </div>
            </>
          )}
          <button style={s.btn} onClick={salvar} disabled={saving || !form.nome}>
            {saving ? 'Salvando...' : '💾 Salvar template'}
          </button>
        </div>
      </div>
      <div>
        <div style={{ fontSize:14, fontWeight:600, color:C.sub, marginBottom:12 }}>
          Templates disponíveis ({templates.length})
        </div>
        {templates.map(t => (
          <div key={t.id} style={{ ...s.card, padding:'14px 16px', display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ fontSize:18 }}>
              {t.tipo==='texto'?'📝':t.tipo==='imagem'?'🖼️':t.tipo==='documento'?'📄':t.tipo==='audio'?'🎵':'🎬'}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:C.text, fontWeight:500 }}>{t.nome}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                {(t.conteudo||'').substring(0,60)}{t.conteudo?.length>60?'...':''}
              </div>
            </div>
            <button style={s.btnRed} onClick={() => remover(t.id)}>✕</button>
          </div>
        ))}
        {!templates.length && <div style={{ color:C.muted, fontSize:13 }}>Nenhum template criado ainda.</div>}
      </div>
    </div>
  );
}

// ── Aba Base de Conhecimento ──────────────────────────────────────
function AbaConhecimento() {
  const [bases, setBases]   = useState([]);
  const [form, setForm]     = useState({ titulo:'', tipo:'texto', conteudo:'', instance_name:'' });
  const [arquivo, setArq]   = useState(null);
  const [msg, setMsg]       = useState(null);
  const [saving, setSaving] = useState(false);

  const carregar = () => api.get('/campanhas/conhecimento').then(r => setBases(r.data));
  useEffect(() => { carregar(); }, []);

  const salvar = async () => {
    setSaving(true); setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if(v) fd.append(k,v); });
      if (arquivo) fd.append('arquivo', arquivo);
      await api.post('/campanhas/conhecimento', fd, { headers:{'Content-Type':'multipart/form-data'} });
      setMsg({ ok:true, txt:'Adicionado à base de conhecimento!' });
      setForm({ titulo:'', tipo:'texto', conteudo:'', instance_name:'' });
      setArq(null);
      carregar();
    } catch (e) { setMsg({ ok:false, txt: e.response?.data?.error || 'Erro' }); }
    finally { setSaving(false); }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover este item?')) return;
    await api.delete(`/campanhas/conhecimento/${id}`);
    carregar();
  };

  return (
    <div style={s.grid2}>
      <div style={s.card}>
        <div style={{ fontSize:14, fontWeight:600, color:C.sub, marginBottom:14 }}>
          Adicionar ao conhecimento do agente
        </div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:14, lineHeight:1.6 }}>
          📚 O agente usará estes documentos para responder perguntas dos clientes com base no seu conteúdo.
        </div>
        {msg && <div style={msg.ok?s.ok:s.err}>{msg.txt}</div>}
        <label style={s.label}>Título</label>
        <input style={s.input} value={form.titulo} placeholder="ex: FAQ Grupo D1, Preços 2025..."
          onChange={e=>setForm(f=>({...f,titulo:e.target.value}))} />
        <label style={s.label}>Tipo</label>
        <select style={s.sel} value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))}>
          <option value="texto">Texto direto</option>
          <option value="pdf">PDF (extração automática)</option>
          <option value="url">URL (endereço web)</option>
        </select>
        {form.tipo === 'texto' && (
          <>
            <label style={s.label}>Conteúdo</label>
            <textarea style={{ ...s.tarea, minHeight:140 }} value={form.conteudo}
              placeholder="Cole aqui as informações, FAQs, preços, descrições de cursos..."
              onChange={e=>setForm(f=>({...f,conteudo:e.target.value}))} />
          </>
        )}
        {form.tipo === 'pdf' && (
          <>
            <label style={s.label}>Arquivo PDF</label>
            <div style={s.uploadBox} onClick={() => document.getElementById('pdf-upl').click()}>
              {arquivo ? `📄 ${arquivo.name}` : 'Clique para selecionar o PDF'}
              <input id="pdf-upl" type="file" accept=".pdf" style={{ display:'none' }}
                onChange={e => setArq(e.target.files[0])} />
            </div>
          </>
        )}
        {form.tipo === 'url' && (
          <>
            <label style={s.label}>URL</label>
            <input style={s.input} value={form.conteudo} placeholder="https://..."
              onChange={e=>setForm(f=>({...f,conteudo:e.target.value}))} />
          </>
        )}
        <label style={s.label}>Instância (deixe em branco para global)</label>
        <input style={s.input} value={form.instance_name} placeholder="Opcional: ex: grupo-d1"
          onChange={e=>setForm(f=>({...f,instance_name:e.target.value}))} />
        <button style={s.btn} onClick={salvar} disabled={saving || !form.titulo}>
          {saving ? 'Processando...' : '📚 Adicionar à base'}
        </button>
      </div>
      <div>
        <div style={{ fontSize:14, fontWeight:600, color:C.sub, marginBottom:12 }}>
          Base atual ({bases.length} documentos)
        </div>
        {bases.map(b => (
          <div key={b.id} style={{ ...s.card, padding:'14px 16px', display:'flex', gap:12 }}>
            <div style={{ fontSize:18 }}>{b.tipo==='pdf'?'📄':b.tipo==='url'?'🔗':'📝'}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:C.text, fontWeight:500 }}>{b.titulo}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                {b.instance_name || 'Global'} · ~{b.tokens_est||0} tokens
              </div>
              {b.conteudo_preview && (
                <div style={{ fontSize:11, color:'#334155', marginTop:4 }}>
                  {b.conteudo_preview}...
                </div>
              )}
            </div>
            <button style={s.btnRed} onClick={() => remover(b.id)}>✕</button>
          </div>
        ))}
        {!bases.length && <div style={{ color:C.muted, fontSize:13 }}>Base de conhecimento vazia.</div>}
      </div>
    </div>
  );
}

// ── Aba Campanhas ─────────────────────────────────────────────────
function AbaCampanhas({ setRelatorio }) {
  const { socket } = useSocket();
  const [campanhas, setCampanhas]   = useState([]);
  const [templates, setTemplates]   = useState([]);
  const [atendentes, setAtendentes] = useState([]);
  const [criando, setCriando]       = useState(false);
  const [form, setForm]             = useState({
    nome:'', instance_name:ALL_ACTIVE_INSTANCES, intervalo_min_seg:300, intervalo_max_seg:600,
    horario_inicio:'08:00', horario_fim:'18:00',
    opcao_sim_texto:1, opcao_nao_texto:2,
    msg_resposta_sim:'😊 Ótimo! Um atendente entrará em contato em breve!',
    msg_resposta_nao:'😊 Tudo bem! Obrigado pelo retorno. Se mudar de ideia, estamos aqui!',
    msg_resposta_ia:true, template_ids:[], contatos_texto:'', mensagem_texto:''
  });
  const [campanhaArquivo, setCampanhaArquivo] = useState(null);
  const [msg, setMsg]       = useState(null);
  const [importando, setImp]= useState({});
  const [grupos, setGrupos] = useState([]);
  const [grupoSel, setGrupoSel] = useState('');
  const [gruposMsg, setGruposMsg] = useState(null);
  const [carregandoGrupos, setCarregandoGrupos] = useState(false);
  const fileRefs            = useRef({});

  const carregar = () => {
    api.get('/campanhas').then(r => setCampanhas(r.data));
    api.get('/campanhas/templates').then(r => setTemplates(r.data));
    const token = localStorage.getItem('waflow_token');
    axios.get(`${window.location.origin}/api/atendentes`, {
      timeout: 15000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(r => {
      const ativosComInstancia = (r.data || []).filter(a => a.instance_name);
      setAtendentes(ativosComInstancia);
      setForm(f => (
        f.instance_name || !ativosComInstancia.length
          ? f
          : { ...f, instance_name: ALL_ACTIVE_INSTANCES }
      ));
    }).catch((e) => {
      setAtendentes([]);
      setMsg({ ok:false, txt: e.response?.data?.error || 'Erro ao carregar numeros de envio.' });
    });
  };
  useEffect(() => { carregar(); }, []);

  // Progresso em tempo real
  useEffect(() => {
    if (!socket) return;
    const h = (data) => {
      setCampanhas(prev => prev.map(c =>
        c.id === data.campanhaId ? { ...c, ...data } : c
      ));
    };
    socket.on('campanha_progresso', h);
    socket.on('campanha_concluida', carregar);
    return () => { socket.off('campanha_progresso', h); socket.off('campanha_concluida', carregar); };
  }, [socket]);

  const criarCampanha = async () => {
    try {
      setMsg(null);
      const mensagemTexto = String(form.mensagem_texto || '').trim();
      let templateIds = [...form.template_ids];

      if (mensagemTexto || campanhaArquivo) {
        const fd = new FormData();
        const tipo = campanhaArquivo
          ? (String(campanhaArquivo.type || '').startsWith('video/') ? 'video' : 'imagem')
          : 'texto';
        fd.append('nome', `Mensagem - ${form.nome || new Date().toLocaleString('pt-BR')}`);
        fd.append('tipo', tipo);
        fd.append('conteudo', mensagemTexto);
        fd.append('caption', mensagemTexto);
        if (campanhaArquivo) fd.append('midia', campanhaArquivo);

        const tpl = await api.post('/campanhas/templates', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        templateIds = [tpl.data.id, ...templateIds];
      }

      if (!templateIds.length) {
        setMsg({ ok:false, txt:'Informe uma mensagem, anexe uma imagem/vídeo ou selecione um template.' });
        return;
      }

      const payload = { ...form, template_ids: templateIds };
      delete payload.mensagem_texto;
      const { data } = await api.post('/campanhas', payload);
      const inseridos = data.contatos_inseridos || 0;
      setMsg({ ok:true, txt: inseridos ? `Campanha criada com ${inseridos} contato(s).` : 'Campanha criada! Agora importe a planilha ou adicione contatos.' });
      setCriando(false);
      setCampanhaArquivo(null);
      setForm(f => ({ ...f, nome:'', instance_name:ALL_ACTIVE_INSTANCES, template_ids:[], contatos_texto:'', mensagem_texto:'' }));
      carregar();
    } catch (e) { setMsg({ ok:false, txt: e.response?.data?.error || 'Erro' }); }
  };

  const contarNumerosColados = (texto) => {
    const vistos = new Set();
    String(texto || '')
      .split(/[\n,;]+/)
      .map(item => item.replace(/\D/g, ''))
      .map(phone => {
        if (phone.startsWith('0')) phone = phone.replace(/^0+/, '');
        if (phone.length === 10 || phone.length === 11) phone = `55${phone}`;
        return phone;
      })
      .filter(phone => phone.length >= 12 && phone.length <= 13)
      .forEach(phone => vistos.add(phone));
    return vistos.size;
  };

  const buscarGrupos = async () => {
    if (!form.instance_name || form.instance_name === ALL_ACTIVE_INSTANCES) {
      setGruposMsg({ ok:false, txt:'Para importar participantes de grupo, escolha um número específico.' });
      return;
    }
    setCarregandoGrupos(true);
    setGruposMsg(null);
    try {
      const { data } = await api.get('/campanhas/grupos', { params:{ instance: form.instance_name } });
      setGrupos(data || []);
      setGrupoSel((data || [])[0]?.id || '');
      setGruposMsg({ ok:true, txt:(data || []).length ? `${data.length} grupo(s) encontrado(s).` : 'Nenhum grupo encontrado nesta instancia.' });
    } catch (e) {
      setGrupos([]);
      setGrupoSel('');
      setGruposMsg({ ok:false, txt:e.response?.data?.error || 'Nao foi possivel buscar os grupos.' });
    } finally {
      setCarregandoGrupos(false);
    }
  };

  const importarGrupo = async () => {
    if (!form.instance_name || form.instance_name === ALL_ACTIVE_INSTANCES || !grupoSel) {
      setGruposMsg({ ok:false, txt:'Selecione um número específico e o grupo.' });
      return;
    }
    setCarregandoGrupos(true);
    setGruposMsg(null);
    try {
      const { data } = await api.get('/campanhas/grupos/participantes', {
        params:{ instance: form.instance_name, groupJid: grupoSel }
      });
      const atuais = String(form.contatos_texto || '')
        .split(/[\n,;]+/)
        .map(item => item.replace(/\D/g, ''))
        .filter(Boolean);
      const vistos = new Set(atuais);
      const novos = (data.contatos || []).map(c => c.phone).filter(phone => {
        if (vistos.has(phone)) return false;
        vistos.add(phone);
        return true;
      });
      setForm(f => ({
        ...f,
        contatos_texto: [...atuais, ...novos].join('\n')
      }));
      setGruposMsg({ ok:true, txt:`${novos.length} numero(s) importado(s) do grupo.` });
    } catch (e) {
      setGruposMsg({ ok:false, txt:e.response?.data?.error || 'Nao foi possivel importar participantes.' });
    } finally {
      setCarregandoGrupos(false);
    }
  };

  const importarExcel = async (campId) => {
    const file = fileRefs.current[campId]?.files[0];
    if (!file) return;
    setImp(p => ({ ...p, [campId]: 'Importando...' }));
    try {
      const fd = new FormData(); fd.append('planilha', file);
      const { data } = await api.post(`/campanhas/${campId}/importar`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setImp(p => ({ ...p, [campId]: `✅ ${data.inseridos} contatos importados` }));
      carregar();
    } catch (e) {
      setImp(p => ({ ...p, [campId]: '❌ ' + (e.response?.data?.error || 'Erro') }));
    }
  };

  const acao = async (campId, acao) => {
    try {
      await api.post(`/campanhas/${campId}/${acao}`);
      carregar();
    } catch (e) { alert(e.response?.data?.error || 'Erro'); }
  };

  const excluirCampanha = async (camp) => {
    if (!window.confirm(`Excluir a campanha "${camp.nome}"?\n\nIsso remove a campanha, contatos importados, fila e respostas dela.`)) return;
    try {
      await api.delete(`/campanhas/${camp.id}`);
      setMsg({ ok:true, txt:'Campanha excluída.' });
      carregar();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao excluir campanha');
    }
  };

  const toggleTpl = (id) => {
    setForm(f => ({
      ...f,
      template_ids: f.template_ids.includes(id)
        ? f.template_ids.filter(x => x !== id)
        : [...f.template_ids, id]
    }));
  };

  const selecionarTodosTpl = () => {
    setForm(f => ({ ...f, template_ids: templates.map(t => t.id) }));
  };

  const limparTpl = () => {
    setForm(f => ({ ...f, template_ids: [] }));
  };

  const templatesSelecionados = templates.filter(t => form.template_ids.includes(t.id));

  const fmtData = (value) => value ? new Date(value).toLocaleString('pt-BR') : '-';
  const num = (value) => Number(value || 0);
  const resumo = campanhas.reduce((acc, c) => ({
    total: acc.total + num(c.total_contatos || c.total_importados),
    enviados: acc.enviados + num(c.enviados || c.total_enviados || c.enviados_ok),
    falhas: acc.falhas + num(c.erros || c.com_erro),
    respostas: acc.respostas + num(c.respondidos || c.total_respostas),
  }), { total:0, enviados:0, falhas:0, respostas:0 });

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:600, color:C.sub }}>
          Campanhas ({campanhas.length})
        </div>
        <button style={s.btn} onClick={() => setCriando(!criando)}>
          {criando ? '✕ Cancelar' : '+ Nova campanha'}
        </button>
      </div>

      {/* Formulário criar campanha */}
      <div style={s.grid4}>
        <KpiCard value={campanhas.length} label="Campanhas criadas" color={C.blue} />
        <KpiCard value={resumo.total} label="Contatos carregados" color={C.purple} />
        <KpiCard value={resumo.enviados} label="Mensagens enviadas" color={C.green} />
        <KpiCard value={resumo.falhas} label="Falhas no envio" color={C.red} />
      </div>

      {criando && (
        <div style={s.card}>
          <div style={{ fontSize:14, fontWeight:600, color:C.sub, marginBottom:14 }}>Nova campanha</div>
          {msg && <div style={msg.ok?s.ok:s.err}>{msg.txt}</div>}
          <div style={s.grid2}>
            <div>
              <label style={s.label}>Nome da campanha</label>
              <input style={s.input} value={form.nome} placeholder="ex: Campanha Maio 2025"
                onChange={e=>setForm(f=>({...f,nome:e.target.value}))} />
              <label style={s.label}>Instância (Evolution API)</label>
              <select style={s.sel} value={form.instance_name}
                onChange={e=>{
                  setForm(f=>({...f,instance_name:e.target.value}));
                  setGrupos([]);
                  setGrupoSel('');
                  setGruposMsg(null);
                }}>
                <option value="">Selecione o numero de envio</option>
                <option value={ALL_ACTIVE_INSTANCES}>Todos os números ativos (rodízio)</option>
                {atendentes.map(a => (
                  <option key={a.id} value={a.instance_name}>
                    {a.name} {a.whatsapp_number ? `- ${a.whatsapp_number}` : ''} ({a.instance_name})
                  </option>
                ))}
              </select>
              {!atendentes.length && (
                <div style={{ color:C.red, fontSize:12, marginTop:-6, marginBottom:13 }}>
                  Nenhum atendente com instancia Evolution ativa encontrado.
                </div>
              )}
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Intervalo mín. (seg)</label>
                  <input style={s.input} type="number" min="3" max="3600" step="1" value={form.intervalo_min_seg}
                    onChange={e=>setForm(f=>({...f,intervalo_min_seg:parseInt(e.target.value, 10)}))} />
                </div>
                <div>
                  <label style={s.label}>Intervalo máx. (seg)</label>
                  <input style={s.input} type="number" min="3" max="3600" step="1" value={form.intervalo_max_seg}
                    onChange={e=>setForm(f=>({...f,intervalo_max_seg:parseInt(e.target.value, 10)}))} />
                </div>
              </div>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Horário início</label>
                  <input style={s.input} type="time" value={form.horario_inicio}
                    onChange={e=>setForm(f=>({...f,horario_inicio:e.target.value}))} />
                </div>
                <div>
                  <label style={s.label}>Horário fim</label>
                  <input style={s.input} type="time" value={form.horario_fim}
                    onChange={e=>setForm(f=>({...f,horario_fim:e.target.value}))} />
                </div>
              </div>
              <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:12, marginBottom:13 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:10 }}>
                  <div>
                    <div style={{ color:C.sub, fontSize:12, fontWeight:700 }}>Importar numeros de grupo</div>
                    <div style={{ color:C.muted, fontSize:11, marginTop:3 }}>
                      O CRM ignora mensagens de grupos, mas voce pode usar os participantes como base de campanha. Para isso escolha um número específico.
                    </div>
                  </div>
                  <button type="button" style={s.btnSm} onClick={buscarGrupos} disabled={carregandoGrupos || !form.instance_name || form.instance_name === ALL_ACTIVE_INSTANCES}>
                    {carregandoGrupos ? 'Buscando...' : 'Buscar grupos'}
                  </button>
                </div>
                {!!grupos.length && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, alignItems:'start' }}>
                    <select style={{ ...s.sel, marginBottom:0 }} value={grupoSel} onChange={e=>setGrupoSel(e.target.value)}>
                      {grupos.map(g => (
                        <option key={g.id} value={g.id}>
                          {g.subject || g.id} {g.participantes ? `(${g.participantes} participantes)` : ''}
                        </option>
                      ))}
                    </select>
                    <button type="button" style={s.btnGrn} onClick={importarGrupo} disabled={carregandoGrupos || !grupoSel}>
                      Importar
                    </button>
                  </div>
                )}
                {gruposMsg && (
                  <div style={{ color:gruposMsg.ok?C.green:C.red, fontSize:12, marginTop:8 }}>
                    {gruposMsg.txt}
                  </div>
                )}
              </div>
              <label style={s.label}>Numeros que receberao a campanha</label>
              <textarea style={{ ...s.tarea, minHeight:110 }} value={form.contatos_texto}
                placeholder={'Cole um numero por linha, ou separados por virgula. Ex:\\n14999999999\\n5514988887777'}
                onChange={e=>setForm(f=>({...f,contatos_texto:e.target.value}))} />
              <div style={{ color:C.muted, fontSize:12, marginTop:-8, marginBottom:13 }}>
                {contarNumerosColados(form.contatos_texto)} numero(s) valido(s) detectado(s). Tambem sera possivel importar Excel depois.
              </div>
            </div>
            <div>
              <label style={s.label}>Respostas automáticas — número para SIM</label>
              <input style={s.input} type="number" value={form.opcao_sim_texto}
                onChange={e=>setForm(f=>({...f,opcao_sim_texto:parseInt(e.target.value)}))} />
              <label style={s.label}>Número para NÃO</label>
              <input style={s.input} type="number" value={form.opcao_nao_texto}
                onChange={e=>setForm(f=>({...f,opcao_nao_texto:parseInt(e.target.value)}))} />
              <label style={{ ...s.label, display:'flex', gap:8, alignItems:'center', marginTop:12, cursor:'pointer' }}>
                <input type="checkbox" checked={!!form.msg_resposta_ia}
                  onChange={e=>setForm(f=>({...f,msg_resposta_ia:e.target.checked}))} />
                Responder usando IA + Base IA
              </label>
              <div style={{ color:C.muted, fontSize:12, lineHeight:1.45, marginTop:4 }}>
                Quando ativo, respostas 1, 2 ou perguntas livres usam a Base IA da instancia.
                Se a IA falhar por cota ou chave, nenhuma resposta pronta sera enviada.
                A chave Gemini fica em Gestor IA &gt; Configurar, na instancia escolhida para envio.
              </div>
              {!form.msg_resposta_ia && (
                <>
                  <label style={s.label}>Resposta automatica para SIM</label>
                  <textarea style={s.tarea} value={form.msg_resposta_sim}
                    onChange={e=>setForm(f=>({...f,msg_resposta_sim:e.target.value}))} />
                  <label style={s.label}>Resposta automatica para NAO</label>
                  <textarea style={s.tarea} value={form.msg_resposta_nao}
                    onChange={e=>setForm(f=>({...f,msg_resposta_nao:e.target.value}))} />
                </>
              )}
            </div>
          </div>
          <div style={s.hr} />
          <div style={{ fontSize:13, color:C.sub, fontWeight:600, marginBottom:10 }}>
            Mensagem que será enviada
          </div>
          <div style={s.grid2}>
            <div>
              <label style={s.label}>Texto da mensagem</label>
              <textarea style={{ ...s.tarea, minHeight:120 }} value={form.mensagem_texto}
                placeholder={'Digite aqui a mensagem principal da campanha. Você pode usar {nome}, {telefone}, {cidade} ou {empresa}.'}
                onChange={e=>setForm(f=>({...f,mensagem_texto:e.target.value}))} />
            </div>
            <div>
              <label style={s.label}>Imagem ou vídeo da mensagem</label>
              <div style={{ ...s.uploadBox, padding:'18px' }} onClick={() => document.getElementById('campanha-midia-upl').click()}>
                {campanhaArquivo ? `📎 ${campanhaArquivo.name}` : 'Clique para anexar imagem ou vídeo'}
              </div>
              <input id="campanha-midia-upl" type="file" accept="image/*,video/*" style={{ display:'none' }}
                onChange={e => setCampanhaArquivo(e.target.files?.[0] || null)} />
              {campanhaArquivo && (
                <button type="button" style={s.btnSm} onClick={() => setCampanhaArquivo(null)}>
                  Remover anexo
                </button>
              )}
              <div style={{ color:C.muted, fontSize:12, lineHeight:1.45, marginTop:8 }}>
                Se houver anexo, o texto será enviado como legenda da imagem ou vídeo. Se não houver anexo, será enviada uma mensagem de texto.
              </div>
            </div>
          </div>
          <div style={s.hr} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, marginBottom:10 }}>
            <div>
              <div style={{ fontSize:13, color:C.sub, fontWeight:600 }}>
                Templates extras da campanha
              </div>
              <div style={{ color:C.muted, fontSize:12, marginTop:3 }}>
                Selecione 1 ou mais. Para enviar imagem ou vídeo, crie um template do tipo Imagem ou Vídeo na aba Templates e selecione aqui.
              </div>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button type="button" style={{ ...s.btn2, padding:'7px 10px' }} onClick={selecionarTodosTpl}>
                Todos
              </button>
              <button type="button" style={{ ...s.btn2, padding:'7px 10px' }} onClick={limparTpl}>
                Limpar
              </button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:8, marginBottom:12 }}>
            {templates.map(t => {
              const sel = form.template_ids.includes(t.id);
              const ordem = form.template_ids.indexOf(t.id) + 1;
              return (
                <div key={t.id} style={{
                  padding:'10px 12px', borderRadius:8, cursor:'pointer', fontSize:12,
                  border:`1px solid ${sel?C.blue:C.border}`,
                  background: sel?'rgba(14,165,233,.1)':'transparent',
                  color: sel?C.blue:C.sub,
                  minHeight:46,
                  display:'flex',
                  alignItems:'center',
                  gap:8
                }} onClick={() => toggleTpl(t.id)}>
                  <input type="checkbox" checked={sel} readOnly />
                  <span style={{ flex:1, minWidth:0 }}>
                    {sel && <b>{ordem}. </b>}
                    {t.tipo==='texto'?'📝':t.tipo==='imagem'?'🖼️':'📄'} {t.nome}
                  </span>
                </div>
              );
            })}
            {!templates.length && <div style={{ color:C.muted, fontSize:12 }}>Crie templates primeiro.</div>}
          </div>
          {templatesSelecionados.length > 0 && (
            <div style={{ color:C.muted, fontSize:12, marginBottom:16 }}>
              Rotação: {templatesSelecionados.map((t, i) => `${i + 1}. ${t.nome}`).join(' → ')}
            </div>
          )}
          <button style={s.btn} onClick={criarCampanha}
            disabled={!form.nome || !form.instance_name || (!form.template_ids.length && !String(form.mensagem_texto || '').trim() && !campanhaArquivo)}>
            Criar campanha
          </button>
        </div>
      )}

      {/* Lista de campanhas */}
      {!campanhas.length && (
        <div style={{ ...s.card, color:C.muted, fontSize:13 }}>
          Nenhuma campanha criada ainda. Quando uma campanha for criada, este painel mostrara data, numero disparador, enviados, falhas, respostas e progresso separados das conversas dos atendentes.
        </div>
      )}
      {campanhas.map(c => {
        const total = num(c.total_contatos || c.total_importados);
        const enviados = num(c.enviados || c.total_enviados || c.enviados_ok);
        const falhas = num(c.erros || c.com_erro);
        const respostas = num(c.respondidos || c.total_respostas);
        const pendentes = num(c.pendentes);
        const pct = total ? (enviados / total) * 100 : 0;
        return (
          <div key={c.id} style={s.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:C.text }}>{c.nome}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                {nomeInstanciaCampanha(c.instance_name)} · {c.total_contatos} contatos
                </div>
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
                  Criada em {fmtData(c.created_at)} · Ultimo envio {fmtData(c.ultimo_envio_em || c.iniciada_em)}
                </div>
                <div style={{ fontSize:11, color:C.sub, marginTop:4 }}>
                  Numero disparador: {nomeInstanciaCampanha(c.instance_name)}
                </div>
              </div>
              <span style={s.badge(STATUS_COLOR[c.status]||'#64748b')}>
                {STATUS_PT[c.status] || c.status}
              </span>
            </div>

            {/* Progresso */}
            <div style={s.grid4}>
              <KpiCard value={total} label="Receberam/na fila" color={C.purple} />
              <KpiCard value={enviados} label="Mensagens enviadas" color={C.blue} />
              <KpiCard value={falhas} label="Falharam" color={C.red} />
              <KpiCard value={c.nao_count||0}    label="Responderam NÃO" color={C.red} />
            </div>
            <ProgressBar value={enviados} max={total || 1} color={C.blue} />
            <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginTop:8, fontSize:12, color:C.muted }}>
              <span>Pendentes: {pendentes}</span>
              <span>Respostas: {respostas}</span>
              <span>SIM: {num(c.sim_count || c.responderam_sim)}</span>
              <span>NAO: {num(c.nao_count || c.responderam_nao)}</span>
              <span>Outras respostas: {num(c.responderam_outro)}</span>
              <span>Progresso: {pct.toFixed(0)}%</span>
            </div>

            {/* Importar Excel */}
            {['rascunho','pausada'].includes(c.status) && (
              <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:10 }}>
                <input ref={el => fileRefs.current[c.id] = el} type="file"
                  accept=".xlsx,.xls,.csv" style={{ display:'none' }}
                  id={`xl-${c.id}`} onChange={() => importarExcel(c.id)} />
                <button style={s.btnSm} onClick={() => document.getElementById(`xl-${c.id}`).click()}>
                  📊 Importar planilha Excel
                </button>
                {importando[c.id] && <span style={{ fontSize:12, color:C.sub }}>{importando[c.id]}</span>}
              </div>
            )}

            {/* Ações */}
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              {c.status === 'rascunho'   && <button style={s.btnGrn} onClick={() => acao(c.id,'iniciar')}>▶ Iniciar</button>}
              {c.status === 'pausada'    && <button style={s.btnGrn} onClick={() => acao(c.id,'iniciar')}>▶ Retomar</button>}
              {c.status === 'rodando'    && <button style={s.btnYel} onClick={() => acao(c.id,'pausar')}>⏸ Pausar</button>}
              {!['cancelada','concluida'].includes(c.status) &&
                <button style={s.btnRed} onClick={() => acao(c.id,'cancelar')}>✕ Cancelar</button>}
              <button style={s.btnSm} onClick={() => setRelatorio(c.id)}>📊 Relatório</button>
              <a href={`${process.env.REACT_APP_API_URL || window.location.origin}/api/campanhas/${c.id}/exportar`}
                style={{ ...s.btnSm, textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
                ⬇ Exportar CSV
              </a>
              {c.status !== 'rodando' && (
                <button style={s.btnRed} onClick={() => excluirCampanha(c)}>Excluir</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Aba Relatório ─────────────────────────────────────────────────
function AbaRelatorio({ campanhaId, onVoltar }) {
  const [rel, setRel]   = useState(null);
  const [tab, setTab]   = useState('resumo');
  const [conts, setConts] = useState([]);
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    if (!campanhaId) return;
    api.get(`/campanhas/${campanhaId}/relatorio`).then(r => setRel(r.data));
    api.get(`/campanhas/${campanhaId}/contatos?limit=200`).then(r => setConts(r.data));
  }, [campanhaId]);

  if (!rel) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Carregando...</div>;

  const c = rel.campanha;
  const filtrados = conts.filter(ct =>
    !filtro || ct.phone.includes(filtro) || (ct.nome||'').toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button style={s.btnSm} onClick={onVoltar}>← Voltar</button>
        <div style={{ fontSize:18, fontWeight:600, color:C.text }}>{c.nome}</div>
        <span style={s.badge(STATUS_COLOR[c.status])}>{STATUS_PT[c.status]}</span>
      </div>

      {/* KPIs principais */}
      <div style={s.grid4}>
        <KpiCard value={c.total_contatos}         label="Total contatos" />
        <KpiCard value={`${c.taxa_resposta}%`}    label="Taxa de resposta"   color={C.blue} />
        <KpiCard value={`${c.taxa_conversao}%`}   label="Taxa de conversão"  color={C.green} />
        <KpiCard value={c.sim_count}              label="Interessados (SIM)" color={C.green} />
      </div>

      <div style={s.tabs}>
        {[['resumo','Resumo'],['contatos','Contatos'],['respostas','Respostas']].map(([k,l]) => (
          <button key={k} style={s.tab(tab===k)} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'resumo' && (
        <div style={s.grid2}>
          <div style={s.card}>
            <div style={{ fontSize:13, color:C.sub, marginBottom:12, fontWeight:600 }}>STATUS DOS ENVIOS</div>
            {rel.por_status.map(r => (
              <div key={r.status} style={{ display:'flex', justifyContent:'space-between',
                padding:'6px 0', borderBottom:`1px solid #1e293b` }}>
                <span style={s.badge(CONT_COLOR[r.status]||'#64748b')}>{r.status.replace(/_/g,' ')}</span>
                <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>{r.total}</span>
              </div>
            ))}
          </div>
          <div style={s.card}>
            <div style={{ fontSize:13, color:C.sub, marginBottom:12, fontWeight:600 }}>RESPOSTAS RECEBIDAS</div>
            {rel.por_resposta.map(r => (
              <div key={r.classificacao} style={{ display:'flex', justifyContent:'space-between',
                padding:'6px 0', borderBottom:`1px solid #1e293b` }}>
                <span style={s.badge(r.classificacao==='sim'?C.green:r.classificacao==='nao'?C.red:C.yellow)}>
                  {r.classificacao}
                </span>
                <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>{r.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'contatos' && (
        <div style={s.card}>
          <input style={{ ...s.input, marginBottom:12 }} placeholder="Filtrar por nome ou telefone..."
            value={filtro} onChange={e => setFiltro(e.target.value)} />
          <table style={s.table}>
            <thead><tr>
              {['Telefone','Nome','Status','Resposta','Enviado em','Respondido em'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtrados.slice(0,100).map(ct => (
                <tr key={ct.id}>
                  <td style={s.td}>{ct.phone}</td>
                  <td style={s.td}>{ct.nome||'–'}</td>
                  <td style={s.td}><span style={s.badge(CONT_COLOR[ct.status]||'#64748b')}>{ct.status.replace(/_/g,' ')}</span></td>
                  <td style={s.td}>{ct.resposta_raw||'–'}</td>
                  <td style={s.td}>{ct.enviado_em?new Date(ct.enviado_em).toLocaleString('pt-BR'):''}</td>
                  <td style={s.td}>{ct.respondido_em?new Date(ct.respondido_em).toLocaleString('pt-BR'):''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'respostas' && (
        <div style={s.card}>
          <table style={s.table}>
            <thead><tr>
              {['Telefone','Mensagem do cliente','Classificação','Resposta da IA','Hora'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rel.por_resposta.length > 0
                ? rel.por_resposta.map((r,i) => (
                    <tr key={i}>
                      <td style={s.td} colSpan={5} style={{ color:C.muted, padding:'20px 8px', textAlign:'center' }}>
                        {r.classificacao}: {r.total} respostas
                      </td>
                    </tr>
                  ))
                : <tr><td colSpan={5} style={{ ...s.td, textAlign:'center', color:C.muted }}>Sem respostas ainda</td></tr>
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────
export default function Campanhas() {
  const [aba, setAba]           = useState('campanhas');
  const [relatorioId, setRel]   = useState(null);

  return (
    <div>
      <div style={s.title}>Campanhas Inteligentes</div>
      <div style={s.sub}>Disparo em massa com intervalos aleatórios · Respostas automáticas SIM/NÃO · IA com base de conhecimento</div>

      {relatorioId ? (
        <AbaRelatorio campanhaId={relatorioId} onVoltar={() => setRel(null)} />
      ) : (
        <>
          <div style={s.tabs}>
            {[['campanhas','📢 Campanhas'],['templates','📝 Templates'],['conhecimento','📚 Base IA']].map(([k,l]) => (
              <button key={k} style={s.tab(aba===k)} onClick={() => setAba(k)}>{l}</button>
            ))}
          </div>
          {aba === 'campanhas'   && <AbaCampanhas setRelatorio={setRel} />}
          {aba === 'templates'   && <AbaTemplates />}
          {aba === 'conhecimento'&& <AbaConhecimento />}
        </>
      )}
    </div>
  );
}
